import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Search, Check, ChevronDown, Loader2, X } from 'lucide-react';
import type { LookupItem } from '../domains/apprenants/hooks/useLookups.js';

interface Props {
  /** Liste d'items à chercher dans */
  items: LookupItem[];
  /** ID sélectionné */
  value: string | null;
  /** Callback quand l'utilisateur sélectionne (id, item complet) */
  onSelect: (id: string | null, item: LookupItem | null) => void;
  /** Placeholder input search */
  placeholder?: string;
  /** Loading state du fetch des items */
  isLoading?: boolean;
  /** Rend personnalisé pour chaque item (par défaut label + sublabel) */
  renderItem?: (item: LookupItem) => ReactNode;
  /** Autorise-t-on de vider la sélection ? */
  clearable?: boolean;
  /** Largeur du dropdown ouvert */
  className?: string;
}

/**
 * Dropdown avec recherche live (filter sur label + sublabel, case-insensitive,
 * normalize accents). Sélection click → onSelect remonte l'ID + l'item.
 *
 * UX :
 *  - Click : ouvre le panneau
 *  - Tape : filtre live
 *  - Click sur un item : sélectionne + ferme
 *  - Escape ou click extérieur : ferme sans changer
 *  - Si selected → affiche le label + bouton clear (si clearable)
 */
export function SearchableLookup({
  items,
  value,
  onSelect,
  placeholder = 'Rechercher…',
  isLoading,
  renderItem,
  clearable = true,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = items.find((i) => i.id === value) ?? null;

  // Click extérieur = fermer
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input à l'ouverture
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const filtered = search.trim()
    ? items.filter((it) => {
        const q = norm(search.trim());
        return norm(it.label).includes(q) || (it.sublabel && norm(it.sublabel).includes(q));
      })
    : items;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="hover:border-primary-400 focus:border-primary-500 flex w-full items-center justify-between gap-1 rounded border border-neutral-300 bg-white px-2 py-1.5 text-left text-xs focus:outline-none"
      >
        <span className={selected ? 'truncate text-neutral-900' : 'truncate text-neutral-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1">
          {selected && clearable && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(null, null);
              }}
              className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-rose-600"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown
            className={`h-3 w-3 flex-shrink-0 text-neutral-400 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute z-[10001] mt-1 flex max-h-[32rem] w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl">
          {/* Search input */}
          <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-neutral-100 px-2 py-2">
            <Search className="h-3.5 w-3.5 text-neutral-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setSearch('');
                }
              }}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm placeholder:text-neutral-400 focus:outline-none"
            />
            {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
          </div>

          {/* List — agrandi : ~12-15 items visibles avant scroll */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs italic text-neutral-400">
                {isLoading ? 'Chargement…' : 'Aucun résultat'}
              </div>
            ) : (
              <ul>
                {filtered.map((it) => {
                  const isSel = it.id === value;
                  return (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onSelect(it.id, it);
                          setOpen(false);
                          setSearch('');
                        }}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                          isSel
                            ? 'bg-primary-50 text-primary-900'
                            : 'text-neutral-800 hover:bg-neutral-50'
                        }`}
                      >
                        <div className="flex-1 truncate">
                          {renderItem ? (
                            renderItem(it)
                          ) : (
                            <>
                              <div className="font-medium">{it.label}</div>
                              {it.sublabel && (
                                <div className="truncate text-[10px] text-neutral-500">
                                  {it.sublabel}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {isSel && <Check className="text-primary-600 h-3 w-3 flex-shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex-shrink-0 border-t border-neutral-100 bg-neutral-50 px-2 py-1 text-[10px] text-neutral-500">
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            {items.length !== filtered.length && ` affichés / ${items.length} total`}
          </div>
        </div>
      )}
    </div>
  );
}
